import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { User } from 'oidc-client-ts'
import { authService } from './AuthService'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    authService
      .getUser()
      .then(user => {
        // If user exists but is expired, remove it immediately
        if (user && user.expired) {
          authService
            .getUserManager()
            .removeUser()
            .then(() => {
              setUser(null)
              setIsLoading(false)
            })
        } else {
          setUser(user)
          setIsLoading(false)
        }
      })
      .catch(error => {
        console.error('Error loading user:', error)
        setIsLoading(false)
      })

    // Listen for user loaded events (including silent renewals)
    const handleUserLoaded = (user: User) => {
      setUser(user)
      setIsLoading(false)
    }

    const handleUserUnloaded = () => {
      setUser(null)
    }

    // These event listeners will trigger when the user manager loads or unloads users
    authService.getUserManager().events.addUserLoaded(handleUserLoaded)
    authService.getUserManager().events.addUserUnloaded(handleUserUnloaded)

    return () => {
      authService.getUserManager().events.removeUserLoaded(handleUserLoaded)
      authService.getUserManager().events.removeUserUnloaded(handleUserUnloaded)
    }
  }, [])

  const login = async () => {
    await authService.signinRedirect()
  }

  const logout = async () => {
    try {
      await authService.signoutRedirect()
      sessionStorage.clear()
      window.location.href = '/'
    } catch (error) {
      console.error('Logout error:', error)
      sessionStorage.clear()
      window.location.href = '/'
    }
  }

  // Derive isAuthenticated from user state and token validity
  const isAuthenticated = user !== null && !user.expired

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
